<script setup>
// Astra DS · CornerTag — la etiqueta de esquina.
//
// Figma: CornerTag 641:195 (átomo, `02 — Components`).
//
// «Fondo lienzo + hairline hacia el interior de la imagen. Anchor=bottom lleva
// la regla arriba; Anchor=top la lleva abajo.»
//
// El borde apunta siempre hacia dentro de la imagen, nunca hacia el borde del
// hueco: pegada a la esquina inferior izquierda, la línea va arriba y a la
// derecha. Si fuera al revés dibujaría una línea contra el borde del contenedor
// y se leería como un fallo de recorte.
import { ref, computed, onMounted, onUpdated } from "vue";

const props = defineProps({
  anchor: {
    type: String,
    default: "bottom",
    validator: (v) => ["bottom", "top"].includes(v),
  },
  // Desplazamiento desde el borde izquierdo, en px. Lo pide el pie de foto del
  // bloque de emisión, que arranca donde acaba la portada encajada.
  offset: { type: Number, default: 0 },

  // A qué lado se pega. El rótulo del programa va a la derecha del bloque de
  // emisión, que es el único sitio donde hay dos etiquetas en la misma banda y
  // no pueden salir del mismo borde.
  side: {
    type: String,
    default: "left",
    validator: (v) => ["left", "right"].includes(v),
  },

  // Si el texto no cabe, al pasar por encima se desplaza para poder leerlo
  // entero. Lo pide el pie de la carátula, que dice «Artista — Álbum [Año]» y
  // se corta con nombres largos.
  //
  // Es opcional a propósito: la mayoría de etiquetas de esquina son cortas
  // —«Álbum», «Último en TV»— y no deben moverse nunca.
  marquee: { type: Boolean, default: false },
});

const caja = ref(null);
const texto = ref(null);
const sobra = ref(0);

// Velocidad constante, no duración fija: con una duración fija un pie el doble
// de largo se leería al doble de velocidad, que es justo lo que no se puede
// hacer con un texto que hay que leer.
const PX_POR_SEGUNDO = 45;

const duracion = computed(() => `${(sobra.value / PX_POR_SEGUNDO).toFixed(2)}s`);

// Solo se anima si REALMENTE no cabe. Medir es la única forma de saberlo: el
// mismo pie cabe con un artista corto y desborda con uno largo.
function medir() {
  if (!props.marquee || !caja.value || !texto.value) return;
  const exceso = texto.value.scrollWidth - caja.value.clientWidth;
  sobra.value = exceso > 1 ? exceso : 0;
}

onMounted(medir);
// El pie cambia con cada tema que suena, así que hay que volver a medir: si no,
// un título largo detrás de uno corto se quedaría sin desplazamiento.
onUpdated(medir);
</script>

<template>
  <span
    ref="caja"
    class="corner-tag"
    :class="[
      `corner-tag--${anchor}`,
      `corner-tag--${side}`,
      { 'corner-tag--marquee': marquee, 'is-overflow': sobra > 0 },
    ]"
    :style="{
      ...(offset ? { left: `${offset}px` } : {}),
      '--arranque': `${offset}px`,
      '--sobra': `${sobra}px`,
      '--marquee-t': duracion,
    }"
  >
    <span ref="texto" class="corner-tag__text">
      <slot />
    </span>
  </span>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.corner-tag {
  @include t.mono-micro;
  position: absolute;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 5px 9px;
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  white-space: nowrap;

  // El texto va en su propia capa para poder moverlo sin arrastrar el fondo ni
  // los filetes de la etiqueta.
  .corner-tag__text {
    display: inline-block;
  }

  // ── El pie que no cabe ──
  //
  // Con `marquee`, la etiqueta deja de crecer más allá de su hueco y esconde lo
  // que sobra. Al pasar por encima, el texto se desplaza EXACTAMENTE lo que
  // sobra y se queda ahí mientras el ratón siga encima; al salir, vuelve.
  //
  // No es un bucle infinito a propósito: un pie de foto dando vueltas sin parar
  // se lee como un letrero de neón, no como un dato. Se mueve cuando alguien
  // quiere leerlo, y se está quieto el resto del tiempo.
  // El hueco disponible NO es el 100% cuando la etiqueta arranca desplazada: en
  // el bloque de emisión el pie empieza donde acaba la carátula encajada, así
  // que hay que descontar ese arranque o la etiqueta se saldría por la derecha.
  &--marquee {
    max-width: calc(100% - var(--arranque, 0px));
    overflow: hidden;
  }

  &--marquee.is-overflow {
    cursor: default;

    .corner-tag__text {
      transition: transform var(--marquee-t, 2s) linear;
    }

    &:hover .corner-tag__text,
    &:focus-visible .corner-tag__text {
      transform: translateX(calc(var(--sobra) * -1));
    }
  }

  // La línea apunta siempre hacia DENTRO del hueco, nunca contra el borde del
  // contenedor: pegada a la izquierda cierra por la derecha, y al revés.
  &--left {
    left: 0;
    border-right: 1px solid var(--color-border-default);
  }

  &--right {
    right: 0;
    border-left: 1px solid var(--color-border-default);
  }

  &--bottom {
    bottom: 0;
    border-top: 1px solid var(--color-border-default);
  }

  &--top {
    top: 0;
    border-bottom: 1px solid var(--color-border-default);
  }
}

// Quien pide no animar no ve el desplazamiento. El texto sigue estando: lo
// que se pierde es poder leer el final, y para eso está el atributo `title`
// que pone quien usa la etiqueta.
@media (prefers-reduced-motion: reduce) {
  .corner-tag--marquee.is-overflow .corner-tag__text {
    transition: none;
  }
}
</style>